/**
 * A video consultation's call link as the panel draws it: where „Dołącz do rozmowy" goes and the host
 * printed under it. `https:` only and no host allow-list — the trainer typed it, so the host is shown
 * instead (consultation-calls plan, PR 2) — and what the session panel offers about it. Pure.
 */

import { HTTPS_LINK_MAX_LENGTH, httpsLinkOf, shownHost, type HttpsLink } from '~/shared/lib/httpsLink';

import type { BookingSession } from './bookingSession';

export function callLinkView(url: string | null | undefined): HttpsLink | null {
  if (typeof url !== 'string') return null;
  const link = httpsLinkOf(url.trim());
  if (link === null) return null;
  return { href: link.href, host: shownHost(link.host) };
}

/** The server's own cap on a pasted link (consultation-calls R2). */
export const CALL_LINK_MAX_LENGTH = HTTPS_LINK_MAX_LENGTH;

/** A pasted link the server would take (`shared/lib/httpsLink`, the server's `CallLinks.Accept`), trimmed. */
export function checkCallLink(input: string): string | null {
  const link = input.trim();
  return httpsLinkOf(link) === null ? null : link;
}

/**
 * Whether the trainer's Google calendar will make a Meet: connected and working; not connected;
 * not offered in this environment at all; or not read yet.
 */
export type GoogleMeetSource = 'connected' | 'off' | 'unavailable' | 'unknown';

/** The connection read (`GET /me/calendar-connections/google`) as a Meet source. */
export function googleMeetSourceOf(read: {
  readonly isError: boolean;
  readonly data?: { readonly available: boolean; readonly connected: boolean; readonly status?: string | null };
}): GoogleMeetSource {
  // An answer in hand outlives a failed re-read; with none, a failure says nothing either way:
  // pasting is offered, connecting is not hinted at.
  if (read.data === undefined) return read.isError ? 'unavailable' : 'unknown';
  if (!read.data.available) return 'unavailable';
  return read.data.connected && read.data.status === 'Active' ? 'connected' : 'off';
}

export type CallLinkOrigin = 'GoogleMeet' | 'Standing' | 'Manual';

/** Where a link came from, in the trainer's words. */
export const CALL_LINK_ORIGIN_KEY: Readonly<Record<CallLinkOrigin, string>> = {
  GoogleMeet: 'web:booking.call.origin.googleMeet',
  Standing: 'web:booking.call.origin.standing',
  Manual: 'web:booking.call.origin.manual',
};

export type CallLinkState =
  | {
      readonly kind: 'join';
      readonly link: HttpsLink;
      readonly origin: CallLinkOrigin | null;
      /** The trainer may paste another link over it. */
      readonly changeable: boolean;
      /** A pasted or standing link is removed on its own; a Meet goes with the video call (R4). */
      readonly removable: boolean;
      /** „Użyj Google Meet": the trainer's Google makes one, and this link is not one. */
      readonly useMeet: boolean;
    }
  /** Google is making the Meet the session asked for: said, and the session read again for a while. */
  | { readonly kind: 'meetPending' }
  /** No link and none coming: the trainer pastes one, asks for a Meet, or is told Google would make it. */
  | { readonly kind: 'add'; readonly connectHint: boolean; readonly useMeet: boolean };

export interface CallPanel {
  /** The „Rozmowa wideo" switch, where the trainer may still change it; `confirmOff` when a link would go. */
  readonly toggle: { readonly on: boolean; readonly confirmOff: boolean } | null;
  readonly link: CallLinkState | null;
}

/**
 * What the session panel says and offers about a call. A consultation's call is the trainer's to
 * change while it is neither settled nor called off (R1); after that only a link already there is
 * offered. A Meet is awaited only where the session asked for one (`callLinkSource: Meet`) and the
 * trainer's Google is connected; `meetWaitOver`: Google was given its minute and made nothing.
 * A backend that predates calls is offered nothing to write; while the connection is unread, the
 * link's slot stays empty rather than offering a paste a Meet note would replace.
 */
export function callPanel(
  session: BookingSession,
  context: { readonly google: GoogleMeetSource; readonly meetWaitOver: boolean },
): CallPanel {
  if (session.kind !== 'consultation') return { toggle: null, link: null };
  const editable = session.status === 'scheduled' && session.callsSupported;
  const link = callLinkView(session.callLink);
  const toggle = editable ? { on: session.isVideoCall, confirmOff: link !== null } : null;
  if (!session.isVideoCall) return { toggle, link: null };
  const meetOffered = editable && context.google === 'connected';
  if (link !== null) {
    const origin = session.callLinkOrigin;
    return {
      toggle,
      link: {
        kind: 'join',
        link,
        origin,
        changeable: editable,
        removable: editable && (origin === 'Manual' || origin === 'Standing'),
        useMeet: meetOffered && origin !== 'GoogleMeet',
      },
    };
  }
  if (!editable || context.google === 'unknown') return { toggle, link: null };
  if (meetOffered && session.callLinkSource === 'Meet' && !context.meetWaitOver) {
    return { toggle, link: { kind: 'meetPending' } };
  }
  return { toggle, link: { kind: 'add', connectHint: context.google === 'off', useMeet: meetOffered } };
}

/** Whether the create panel may offer „Rozmowa wideo": no session read so far came from an older backend. */
export function backendOffersCalls(sessions: readonly BookingSession[]): boolean {
  // Nothing read (an empty week) offers it: the older backend on an empty schedule is covered by the release order, backend first.
  return sessions.every((session) => session.callsSupported);
}
